import { useEffect, useEffectEvent, useRef } from 'react';
import type { DependencyList } from 'react';

import { depsIdentityKey } from '@shared/utils/depsIdentity';

import { useChatDockRegistry } from './useChatDockRegistry';

import type { ChatSurfaceDescriptor } from './chatDockTypes';

/**
 * Publish this surface's descriptor to the dock while mounted, keyed by ownerId.
 * Re-publishes when `deps` change so `context()`/`onReady` never go stale.
 * Clears only this owner's entry on unmount.
 *
 * When `descriptor` is null the surface is not registered (or is unregistered if
 * it was previously registered). This lets callers gate registration on a runtime
 * condition (e.g. "only in edit mode") without conditionally calling the hook.
 */
export function useRegisterChatSurface(
  descriptor: ChatSurfaceDescriptor | null,
  deps: DependencyList
): void {
  const register = useChatDockRegistry((s) => s.register);
  const unregister = useChatDockRegistry((s) => s.unregister);
  // Track the last ownerId we registered so we can unregister it on transition to null.
  const lastOwnerIdRef = useRef<string | null>(null);

  const ownerId = descriptor === null ? null : descriptor.ownerId;
  // The descriptor is rebuilt every render; it is re-published on the
  // caller-provided deps, not on its own identity.
  const depsKey = depsIdentityKey(deps);
  const publishDescriptor = useEffectEvent(() => {
    if (descriptor !== null) register(descriptor);
  });

  useEffect(() => {
    if (ownerId === null) {
      // Unregister whatever was previously registered (if anything). No cleanup is
      // returned: lastOwnerIdRef is now null, so a re-run of this null branch is a
      // safe no-op and unmount has nothing to clear.
      if (lastOwnerIdRef.current !== null) {
        unregister(lastOwnerIdRef.current);
        lastOwnerIdRef.current = null;
      }
      return;
    }
    publishDescriptor();
    lastOwnerIdRef.current = ownerId;
    return () => {
      unregister(ownerId);
      lastOwnerIdRef.current = null;
    };
  }, [register, unregister, ownerId, depsKey]);
}
